import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData,
  fields: Record<string, { apiField: string; isBoolean?: boolean }>
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(fields)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else {
        const strVal = currentVal as string;
        changes[config.apiField] = strVal || null;
      }
    }
  }

  return changes;
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        bhPersonalContact
        dobChild1
        dobChild2
        homePhone2Old
        pager
        previousEmployerAndTitle
      }
    }
  }
`;

const PERSONAL_CONTACT_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean }
> = {
  bhPersonalContact: { apiField: 'bhPersonalContact', isBoolean: true },
  dobChild1: { apiField: 'dobChild1' },
  dobChild2: { apiField: 'dobChild2' },
  homePhone2Old: { apiField: 'homePhone2Old' },
  pager: { apiField: 'pager' },
  previousEmployerAndTitle: { apiField: 'previousEmployerAndTitle' }
};

export interface EditPersonalContactDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditPersonalContactDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPersonalContactDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(
    value,
    initialValue,
    PERSONAL_CONTACT_FIELDS
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save personal contact');
      } else {
        toast.success('Personal contact saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving personal contact:', error);
      toast.error('Failed to save personal contact');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Personal Contact"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.bhPersonalContact}
          label="BH Personal Contact"
          onCheckedChange={v => {
            updateField('bhPersonalContact', v);
          }}
        />
        <EditFormField
          label="DOB Child1"
          onChange={v => {
            updateField('dobChild1', v);
          }}
          value={value.dobChild1}
        />
        <EditFormField
          label="DOB Child2"
          onChange={v => {
            updateField('dobChild2', v);
          }}
          value={value.dobChild2}
        />
        <EditFormField
          label="Home Phone 2 Old"
          onChange={v => {
            updateField('homePhone2Old', v);
          }}
          type="tel"
          value={value.homePhone2Old}
        />
        <EditFormField
          label="Pager"
          onChange={v => {
            updateField('pager', v);
          }}
          type="tel"
          value={value.pager}
        />
        <EditFormField
          label="Previous Employer and Title"
          onChange={v => {
            updateField('previousEmployerAndTitle', v);
          }}
          value={value.previousEmployerAndTitle}
        />
      </div>
    </EditDialog>
  );
}
